import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReplayEngine from './ReplayEngine';
import { loadReplay } from './replayLoader';
import { dayWindow, getReplayBounds, nextRecording, prepareReplays, replayTimestamp, selectRecordingAt } from './replayDay';
import { createReplayLink } from './replayLink';
import './replayDay.css';

const clock = (time) => Number.isFinite(time)
  ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
const sameId = (a, b) => String(a) === String(b);
const started = row => replayTimestamp(row.startedAt) ?? row.start;
const source = row => row?.surface === 'web' ? 'Web app'
  : row?.emr && !/^(none|null|unknown)$/i.test(row.emr) ? row.emr : 'Chrome extension';
const timeInput = time => {
  const date = new Date(time);
  return [date.getHours(), date.getMinutes(), date.getSeconds()].map(n => String(n).padStart(2, '0')).join(':');
};
const durationLabel = row => {
  if (row.end === null) return 'Duration unknown';
  const seconds = Math.max(0, Math.round((row.end - row.start) / 1000));
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

/** Keyed ownership: old players unmount before this effect fetches the next body. */
function LoadedRecording({ apiClient, selection, onMetadata, onLoaded, onFailure, onPosition, onEnded, skipInactive }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const callbacks = useRef({ onMetadata, onLoaded, onFailure });
  callbacks.current = { onMetadata, onLoaded, onFailure };
  useEffect(() => {
    const abort = new AbortController();
    loadReplay(apiClient, selection.replayId, abort.signal, replay => callbacks.current.onMetadata(replay))
      .then(result => {
        if (abort.signal.aborted) return;
        const bounds = getReplayBounds(result.events);
        if (!bounds) throw new Error('This recording has no timestamped events.');
        if (callbacks.current.onLoaded(result.replay, bounds)) setData(result);
      })
      .catch(e => {
        if (abort.signal.aborted) return;
        setError(e.message);
        callbacks.current.onFailure(selection.replayId, e.message);
      });
    return () => abort.abort();
  }, [apiClient, selection.replayId]);

  if (error) return <div className="day-empty" role="alert">Could not load this recording: {error}<br />Select a recording above to retry or continue.</div>;
  if (!data) return <div className="day-empty" role="status">Loading recording…</div>;
  return <ReplayEngine replay={data.replay} events={data.events} entry={selection}
    onPosition={onPosition} onEnded={onEnded} skipInactive={skipInactive} />;
}

export default function ReplayDayViewer({ apiClient, initialReplay, initialTimestamp = null, onClose }) {
  const [rows, setRows] = useState(() => prepareReplays([initialReplay]));
  const rowsRef = useRef(rows);
  const [context, setContext] = useState(() => initialReplay.userEmail && initialReplay.startedAt
    ? { ...dayWindow(initialReplay.startedAt), userEmail: initialReplay.userEmail } : null);
  const [selection, setSelection] = useState({ replayId: initialReplay.replayId, timestamp: initialTimestamp, playing: false, speed: 1, key: 0 });
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const counter = useRef(0);
  const [position, setPosition] = useState(initialTimestamp ?? rows[0]?.start ?? 0);
  const positionRef = useRef({ timestamp: position, playing: false, speed: 1 });
  const lastPaint = useRef(0);
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const [jumpTime, setJumpTime] = useState('');
  const navigatorRef = useRef(null);
  const browseRef = useRef(null);
  const [skipIdle, setSkipIdle] = useState(true);
  const skipRef = useRef(skipIdle);
  skipRef.current = skipIdle;
  const [gap, setGap] = useState(null);
  const [notice, setNotice] = useState('');
  const [linkToCopy, setLinkToCopy] = useState('');
  const [indexError, setIndexError] = useState('');
  const [indexRetry, setIndexRetry] = useState(0);
  const indexReady = useRef(false);
  const pendingAdvance = useRef(null);
  const advanceRef = useRef(null);
  const navigateRef = useRef(null);

  useEffect(() => {
    if (!navigatorOpen) return undefined;
    const selected = navigatorRef.current?.querySelector('[aria-current="true"]');
    selected?.focus({ preventScroll: true });
    selected?.scrollIntoView({ block: 'nearest' });
    const dismiss = event => {
      if (event.type === 'keydown') {
        if (event.key !== 'Escape') return;
        browseRef.current?.focus();
      } else if (navigatorRef.current?.contains(event.target)) return;
      setNavigatorOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [navigatorOpen]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  const updateRows = useCallback(update => {
    const next = update(rowsRef.current);
    rowsRef.current = next;
    setRows(next);
  }, []);
  const updatePosition = useCallback(next => {
    if (pendingAdvance.current && next.timestamp < pendingAdvance.current.at.timestamp) pendingAdvance.current = null;
    positionRef.current = next;
    const now = performance.now();
    if (now - lastPaint.current >= 250 || !next.playing) {
      lastPaint.current = now;
      setPosition(next.timestamp);
    }
  }, []);

  const openRecording = useCallback((replay, timestamp, playing = false, automatic = false, seeking = false) => {
    pendingAdvance.current = null;
    setGap(null);
    updateRows(current => current.map(row => sameId(row.replayId, replay.replayId) ? { ...row, unavailable: false } : row));
    const next = { replayId: replay.replayId, timestamp, playing, speed: positionRef.current.speed,
      automatic, seeking, key: ++counter.current };
    selectionRef.current = next;
    setSelection(next);
    if (timestamp !== null) {
      positionRef.current = { ...positionRef.current, timestamp, playing };
      setPosition(timestamp);
    }
  }, [updateRows]);

  const advance = useCallback((at, currentId) => {
    if (!indexReady.current) {
      pendingAdvance.current = { at, currentId };
      return;
    }
    const next = nextRecording(rowsRef.current, at.timestamp, currentId);
    if (!next) {
      selectionRef.current = null;
      setSelection(null);
      setNotice('End of the available recordings for this day.');
      return;
    }
    if (next.timestamp > at.timestamp && !skipRef.current) {
      selectionRef.current = null;
      setSelection(null);
      setGap({ start: at.timestamp, end: next.timestamp, replay: next.replay,
        playing: at.playing, speed: at.speed });
      positionRef.current = at;
      setPosition(at.timestamp);
    } else {
      setNotice(next.timestamp > at.timestamp ? `Skipped gap: ${clock(at.timestamp)} → ${clock(next.timestamp)}` : 'Continuing at the same time in the next recording.');
      openRecording(next.replay, next.timestamp, at.playing, true);
    }
  }, [openRecording]);
  advanceRef.current = advance;

  const onMetadata = useCallback(replay => {
    if (!replay) return;
    setContext(previous => previous || { ...dayWindow(replay.startedAt), userEmail: replay.userEmail });
    const parsed = prepareReplays([replay])[0];
    if (parsed) updateRows(current => {
      const existing = current.find(row => sameId(row.replayId, replay.replayId));
      return [...current.filter(row => !sameId(row.replayId, replay.replayId)), { ...parsed, ...existing }].sort((a, b) => a.start - b.start);
    });
  }, [updateRows]);

  useEffect(() => {
    if (!context?.userEmail || !context.startAt) return undefined;
    let cancelled = false;
    indexReady.current = false;
    setIndexError('');
    apiClient.searchReplays({ userEmail: context.userEmail, startAt: context.startAt, endAt: context.endAt })
      .then(result => {
        if (cancelled) return;
        if (result.error) throw new Error(result.error);
        updateRows(current => {
          const merged = prepareReplays(result.replays || []).map(row => ({ ...row,
            ...current.find(old => sameId(old.replayId, row.replayId)) }));
          for (const row of current) if (!merged.some(other => sameId(other.replayId, row.replayId))) merged.push(row);
          return merged.sort((a, b) => a.start - b.start);
        });
        indexReady.current = true;
        if (pendingAdvance.current) {
          const pending = pendingAdvance.current;
          pendingAdvance.current = null;
          advanceRef.current(pending.at, pending.currentId);
        }
      }).catch(error => { if (!cancelled) setIndexError(`Day timeline unavailable: ${error.message}`); });
    return () => { cancelled = true; };
  }, [apiClient, context, indexRetry, updateRows]);

  const onLoaded = useCallback((replay, bounds) => {
    updateRows(current => current.map(row => sameId(row.replayId, replay.replayId) ? { ...row, ...bounds, verified: true } : row));
    const selected = selectionRef.current;
    if (!selected || !sameId(selected.replayId, replay.replayId)) return false;
    if (selected.seeking && (selected.timestamp < bounds.start || selected.timestamp >= bounds.end)) {
      navigateRef.current(selected.timestamp);
      return false;
    }
    if (selected.timestamp !== null && selected.timestamp >= bounds.end && selected.automatic) {
      advanceRef.current({ timestamp: selected.timestamp, playing: selected.playing, speed: selected.speed }, replay.replayId);
      return false;
    }
    const timestamp = Math.max(bounds.start, Math.min(bounds.end, selected.timestamp ?? bounds.start));
    if (selected.timestamp !== null && Math.abs(timestamp - selected.timestamp) >= 1000) setNotice(`No captured events at ${clock(selected.timestamp)}; showing ${clock(timestamp)}.`);
    positionRef.current = { timestamp, playing: selected.playing, speed: selected.speed };
    setPosition(timestamp);
    return true;
  }, [updateRows]);

  const onFailure = useCallback((replayId, message) => {
    updateRows(current => current.map(row => sameId(row.replayId, replayId) ? { ...row, unavailable: true } : row));
    setNotice(`Recording ${replayId} unavailable: ${message}`);
    const selected = selectionRef.current;
    if (selected?.seeking) navigateRef.current(selected.timestamp);
    else if (selected?.automatic) advanceRef.current({ timestamp: selected.timestamp,
      playing: selected.playing, speed: selected.speed }, replayId);
  }, [updateRows]);
  const onEnded = useCallback(at => {
    positionRef.current = at;
    advanceRef.current(at, selectionRef.current?.replayId);
  }, []);

  const skipGap = useCallback(() => {
    if (gap) openRecording(gap.replay, gap.end, gap.playing, true);
  }, [gap, openRecording]);
  useEffect(() => {
    if (!gap) return undefined;
    if (skipIdle) { skipGap(); return undefined; }
    if (!gap.playing) return undefined;
    const started = performance.now();
    const from = positionRef.current.timestamp;
    const timer = setInterval(() => {
      const timestamp = Math.min(gap.end, from + (performance.now() - started) * gap.speed);
      updatePosition({ timestamp, playing: true, speed: gap.speed });
      if (timestamp >= gap.end) { clearInterval(timer); skipGap(); }
    }, 250);
    return () => clearInterval(timer);
  }, [gap, skipIdle, skipGap, updatePosition]);

  useEffect(() => {
    if (!gap || navigatorOpen) return undefined;
    const onKeyDown = event => {
      if (event.code !== 'Space' || event.repeat || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.defaultPrevented
        || event.target?.isContentEditable
        || event.target?.closest?.('input, textarea, select, button, a[href], [role="button"], [role="textbox"], [role="combobox"], [role="slider"]')) return;
      event.preventDefault();
      positionRef.current = { ...positionRef.current, playing: !gap.playing };
      setGap({ ...gap, playing: !gap.playing });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [gap, navigatorOpen]);

  const navigateTime = time => {
    pendingAdvance.current = null;
    setNotice('');
    const current = selectionRef.current;
    const chosen = selectRecordingAt(rowsRef.current, time, current?.replayId);
    if (chosen && current && sameId(chosen.replayId, current.replayId)) {
      setSelection({ ...current, timestamp: time, playing: positionRef.current.playing, automatic: false, seeking: true });
    } else if (chosen) openRecording(chosen, time, positionRef.current.playing, false, true);
    else {
      const next = nextRecording(rowsRef.current, time, null);
      if (next) {
        setSelection(null);
        selectionRef.current = null;
        setGap({ start: time, end: next.timestamp, replay: next.replay, playing: positionRef.current.playing, speed: positionRef.current.speed });
        positionRef.current = { ...positionRef.current, timestamp: time };
        setPosition(time);
      } else {
        selectionRef.current = null;
        setSelection(null);
        setGap(null);
        positionRef.current = { ...positionRef.current, timestamp: time, playing: false };
        setPosition(time);
        setNotice('No available recording at or after that time.');
      }
    }
  };
  navigateRef.current = navigateTime;

  // Loading confirms playback bounds; the list stays anchored to session starts.
  const orderedRows = useMemo(() => [...rows].sort((a, b) => started(a) - started(b)
    || String(a.replayId).localeCompare(String(b.replayId), 'en', { numeric: true })), [rows]);
  const selectedIndex = orderedRows.findIndex(row => sameId(row.replayId, selection?.replayId));
  const chooseRecording = row => {
    setNavigatorOpen(false);
    browseRef.current?.focus();
    if (sameId(row.replayId, selection?.replayId) && !row.unavailable) return;
    const at = positionRef.current;
    const time = row.start <= at.timestamp && (row.end === null || row.end > at.timestamp) ? at.timestamp : row.start;
    setNotice('');
    openRecording(row, time, at.playing);
  };
  const copyLink = async () => {
    const link = createReplayLink(window.location.href, selection.replayId, positionRef.current.timestamp);
    setLinkToCopy('');
    try {
      await navigator.clipboard.writeText(link);
      setNotice('Link to this recording and time copied.');
    } catch (error) {
      setLinkToCopy(link);
    }
  };

  return <div className="replay-day">
    <header className="day-header">
      <span className="day-brand"><i />Replay</span>
      <div className="day-identity"><strong title={context?.userEmail}>{context?.userEmail || 'Loading…'}</strong><span>{context?.day || ''}</span></div>
      <div className="day-switcher" ref={navigatorRef}>
        <button className="day-step" aria-label="Previous recording" disabled={selectedIndex <= 0}
          onClick={() => chooseRecording(orderedRows[selectedIndex - 1])}>‹</button>
        <button className="day-browse" ref={browseRef} aria-label="Browse recordings" aria-haspopup="dialog"
          aria-expanded={navigatorOpen} aria-controls="day-recordings" data-replay-id={selection?.replayId ?? ''}
          onClick={() => { setJumpTime(timeInput(positionRef.current.timestamp)); setNavigatorOpen(open => !open); }}>
          <span>{selectedIndex < 0 ? `${orderedRows.length} recordings` : `Recording ${selectedIndex + 1} of ${orderedRows.length}`}</span>
          <small>{selectedIndex < 0 ? 'Browse day' : source(orderedRows[selectedIndex])}</small>
          <svg className="day-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m4 6 4 4 4-4" /></svg>
        </button>
        <button className="day-step" aria-label="Next recording" disabled={selectedIndex + 1 >= orderedRows.length}
          onClick={() => chooseRecording(orderedRows[selectedIndex + 1])}>›</button>
        {navigatorOpen && <div className="day-backdrop" aria-hidden="true" onPointerDown={() => {
          setNavigatorOpen(false);
          browseRef.current?.focus();
        }} />}
        {navigatorOpen && <section className="day-navigator" id="day-recordings" role="dialog" aria-label="Day recordings">
          <div className="day-navigator-heading"><div><strong>Recordings</strong><span>{context?.day} · Local time</span></div>
            <button aria-label="Close recordings" onClick={() => { setNavigatorOpen(false); browseRef.current?.focus(); }}>×</button></div>
          <form className="day-jump" onSubmit={event => {
            event.preventDefault();
            if (!jumpTime || !context || !indexReady.current) return;
            const [hours, minutes, seconds = 0] = jumpTime.split(':').map(Number);
            const target = new Date(context.start);
            target.setHours(hours, minutes, seconds, 0);
            navigateTime(target.getTime());
            setNavigatorOpen(false);
            browseRef.current?.focus();
          }}>
            <label htmlFor="day-jump-time">Jump to time</label>
            <input id="day-jump-time" type="time" step="1" required value={jumpTime} onChange={e => setJumpTime(e.target.value)} />
            <button disabled={!context?.startAt || !indexReady.current}>Go</button>
          </form>
          <div className="day-recording-list">
            {orderedRows.map((row, i) => {
              const current = sameId(row.replayId, selection?.replayId);
              const sameTime = !row.unavailable && row.end !== null && row.start <= position && row.end > position;
              return <button key={row.replayId} data-replay-id={row.replayId} aria-current={current ? 'true' : undefined}
                aria-label={`Recording ${i + 1}, ${source(row)}, ${clock(started(row))}`}
                className={`day-recording${row.unavailable ? ' unavailable' : ''}`} onClick={() => chooseRecording(row)}>
                <span className="day-number">{i + 1}</span>
                <span className="day-recording-copy"><span><strong>{source(row)}</strong><time>{clock(started(row))}</time></span>
                  <small>{row.unavailable ? 'Unavailable · select to retry' : `${durationLabel(row)}${row.end === null ? '' : ` · Ends ${clock(row.end)}`}`}<code>#{row.replayId}</code></small></span>
                <span className="day-recording-state">{current ? 'Current' : sameTime ? 'Same time' : ''}</span>
              </button>;
            })}
          </div>
          <footer>{!indexReady.current && !indexError ? 'Loading recordings…' : 'Playback continues automatically. Overlapping recordings open at the current time.'}</footer>
        </section>}
      </div>
      <time className="day-clock" dateTime={new Date(position).toISOString()}>{clock(position)}</time>
      <label title="Shorten pauses over 10 seconds without user interaction, and skip time between recordings."><input type="checkbox" checked={skipIdle} onChange={e => setSkipIdle(e.target.checked)} /> Skip idle time</label>
      <button className="day-share" aria-label="Copy link to current time" title="Copy link to current time"
        disabled={selectedIndex < 0 || !orderedRows[selectedIndex]?.verified} onClick={copyLink}>
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m6 10 4-4M5 6l-2 2a3 3 0 0 0 4 4l2-2M7 6l2-2a3 3 0 0 1 4 4l-2 2" /></svg><span>Copy link</span>
      </button>
      <button className="day-close" onClick={onClose}>Close</button>
    </header>
    <div className="day-notices">
      {linkToCopy && <div className="day-notice" role="status"><label>Copy this link<input aria-label="Link to recording and time" readOnly value={linkToCopy} onFocus={e => e.target.select()} /></label><button aria-label="Dismiss link" onClick={() => setLinkToCopy('')}>×</button></div>}
      {indexError && <div className="day-notice" role="alert">{indexError} <button onClick={() => setIndexRetry(n => n + 1)}>Retry</button></div>}
      {notice && <div className="day-notice" role="status">{notice}<button aria-label="Dismiss message" onClick={() => setNotice('')}>×</button></div>}
    </div>
    <main className="day-player">
      {gap ? <div className="day-empty">No recording in this gap<br />{clock(position)} → {clock(gap.end)}<p>
        <button onClick={() => {
          positionRef.current = { ...positionRef.current, playing: !gap.playing };
          setGap({ ...gap, playing: !gap.playing });
        }}>{gap.playing ? 'Pause' : 'Play'}</button>{' '}
        <button onClick={skipGap}>Skip to next recording</button></p></div>
        : selection ? <LoadedRecording key={selection.key} apiClient={apiClient} selection={selection}
          onMetadata={onMetadata} onLoaded={onLoaded} onFailure={onFailure} onPosition={updatePosition} onEnded={onEnded} skipInactive={skipIdle} />
          : <div className="day-empty">Select a recording to watch.</div>}
    </main>
  </div>;
}
